import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, Shield, Heart, Target, PiggyBank, Briefcase,
  Users, Home, GraduationCap, Plane, Car, Building
} from 'lucide-react';
import { companyInfo, services } from '../data/mock';

const Services = () => {
  const handleWhatsApp = () => {
    window.open(companyInfo.whatsappLink, '_blank');
  };

  const getIcon = (iconName) => {
    const icons = {
      TrendingUp, Shield, Heart, Target, PiggyBank, Briefcase
    };
    const Icon = icons[iconName] || TrendingUp;
    return <Icon className="w-10 h-10" />;
  };

  const goalTypes = [
    { icon: GraduationCap, title: "Children's Education", description: "Plan for rising education costs" },
    { icon: Home, title: "Home Purchase", description: "Build corpus for down payment" },
    { icon: Plane, title: "Dream Vacation", description: "Save for memorable experiences" },
    { icon: Car, title: "Vehicle Purchase", description: "Systematic saving for vehicles" },
    { icon: Building, title: "Real Estate Investment", description: "Wealth creation through property" },
    { icon: Briefcase, title: "Retirement Corpus", description: "Secure your golden years" }
  ];

  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive financial solutions tailored to your life goals and risk profile
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-[#1a1f2e] border-gray-800 hover:border-[#F59E0B] transition-all hover:shadow-lg hover:shadow-[#F59E0B]/10 group">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-[#F59E0B]/10 rounded-xl flex items-center justify-center text-[#F59E0B] mb-6 group-hover:bg-[#F59E0B]/20 transition-colors">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-white">
                    {service.title}
                  </h3>
                  <p className="text-[17px] text-gray-300 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service: Mutual Funds */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/732444/pexels-photo-732444.jpeg"
                alt="Mutual Fund Advisory"
                className="rounded-2xl shadow-xl w-full h-auto border border-gray-800"
              />
            </div>
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Mutual Fund <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Advisory</span>
              </h2>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                Our core expertise lies in helping you navigate the world of mutual funds with confidence and clarity.
              </p>
              <div className="space-y-4">
                {[
                  { title: "Equity Funds", desc: "Long-term wealth creation through carefully selected equity mutual funds" },
                  { title: "Debt Funds", desc: "Stable returns with lower risk for conservative investors" },
                  { title: "Hybrid Funds", desc: "Balanced approach combining equity and debt for optimal returns" },
                  { title: "SIP Planning", desc: "Disciplined investing through systematic investment plans" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start p-4 bg-[#1a1f2e] rounded-lg border border-gray-800">
                    <div className="w-2 h-2 bg-[#F59E0B] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-[17px] text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Services */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Insurance <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Solutions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive protection for you and your family
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="bg-[#1a1f2e] border-2 border-[#F59E0B]/30">
              <CardContent className="p-10">
                <Shield className="w-16 h-16 text-[#F59E0B] mb-6" />
                <h3 className="text-3xl font-bold text-white mb-4">Life Insurance</h3>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Secure your family's financial future with adequate life insurance coverage.
                </p>
                <ul className="space-y-3 text-[17px] text-gray-400">
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Term insurance plans for maximum coverage</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Coverage amount calculation based on your needs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Policy comparison and selection assistance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Claims support and policy servicing</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1f2e] border-2 border-[#F59E0B]/30">
              <CardContent className="p-10">
                <Heart className="w-16 h-16 text-[#F59E0B] mb-6" />
                <h3 className="text-3xl font-bold text-white mb-4">Health Insurance</h3>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Protect yourself from rising medical costs with comprehensive health coverage.
                </p>
                <ul className="space-y-3 text-[17px] text-gray-400">
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Individual and family floater plans</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Critical illness and super top-up policies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Coverage adequacy assessment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#F59E0B] mr-2">•</span>
                    <span>Policy renewal and claims assistance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Goal-Based Planning */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Goal-Based <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Financial Planning</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Every financial goal deserves a dedicated investment strategy
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {goalTypes.map((goal, index) => (
              <Card key={index} className="bg-[#1a1f2e] border-gray-800 hover:border-[#F59E0B] transition-all">
                <CardContent className="p-8 text-center">
                  <goal.icon className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-white">
                    {goal.title}
                  </h3>
                  <p className="text-[17px] text-gray-400">{goal.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Retirement Planning */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Retirement <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Planning</span>
              </h2>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                Build a substantial retirement corpus that ensures financial independence and maintains your lifestyle in your golden years.
              </p>
              <div className="space-y-4">
                {[
                  "Retirement corpus calculation based on your lifestyle",
                  "Tax-efficient investment strategies (PPF, EPF, NPS)",
                  "Asset allocation adjusted as you approach retirement",
                  "Retirement income planning and withdrawal strategies",
                  "Estate planning and wealth transfer guidance"
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <Target className="w-6 h-6 text-[#F59E0B] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-lg text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1622610607501-32ac9c927216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBmYW1pbHl8ZW58MHx8fHwxNzcwMjc2OTMzfDA&ixlib=rb-4.1.0&q=85"
                alt="Retirement Planning"
                className="rounded-2xl shadow-xl w-full h-auto border border-gray-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="bg-gradient-to-r from-[#F59E0B]/10 to-[#FBBF24]/10 border-2 border-[#F59E0B]/30">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Plan Your Financial Future?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let's discuss which services best suit your financial goals and life stage
              </p>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-10 py-6 text-lg"
              >
                Schedule a Consultation
              </Button>
              <p className="text-sm text-gray-500 mt-6">
                {companyInfo.disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Services;